// src/pages/Community/Vehicle/VehicleDetail.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageWrapper from "../../../components/PageWrapper";
import "./css/VehicleWeaponDetail.css";
import {
  LikeBar,
  ReactionBar,
  Bookmark,
  PresenceBadge,
  Discussion,
} from "../components";

// ---------- Utils ----------
function toYouTubeEmbed(u = "") {
  if (!/youtu\.?be/.test(u)) return null;
  try {
    const normalized = u.replace("youtu.be/", "www.youtube.com/watch?v=");
    const url = new URL(normalized);
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    const last = url.pathname.split("/").filter(Boolean).pop();
    return last ? `https://www.youtube.com/embed/${last}` : null;
  } catch {
    return null;
  }
}

function Section({ id, title, children }) {
  const headingId = title ? `${id}-h` : undefined;
  return (
    <section
      id={id}
      className="vw-sec"
      {...(title ? { "aria-labelledby": headingId } : { "aria-label": id })}
    >
      {title && (
        <h2 className="vw-h2" id={headingId}>
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

// ---------- Normalizer (Firestore → UI) ----------
function normalizeVehicle(d = {}) {
  const clean = (v) => (typeof v === "string" ? v.trim() : v);

  const tags = Array.isArray(d.tags)
    ? d.tags
    : d.tags
    ? String(d.tags).split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const gallery = Array.isArray(d.gallery) ? d.gallery : [];
  const image =
    d.coverImage ||
    (gallery.length ? gallery[0] : null) ||
    d.image ||
    d.imageUrl ||
    null;

  return {
    name: d.name ?? d.vehicleName ?? "Unnamed Vehicle",
    portrait: image,
    make: d.make ?? d.manufacturer ?? null,
    class: d.class ?? null,
    price: typeof d.price === "number" ? d.price : d.price ? Number(d.price) : null,
    seats: d.seats ?? null,
    drivetrain: d.drivetrain ?? null, // e.g., "4x4"
    waterCapable: !!d.waterCapable,
    boostNitro: !!d.boostNitro,

    // perf
    topSpeedKph: d.topSpeedKph ?? null,
    accel0to100: d.accel0to100 ?? d["accel0to100(kph)"] ?? null,
    brake100to0m: d.brake100to0m ?? null,
    handling: d.handling ?? null,
    armor: d.armor ?? null,

    // text
    description: clean(d.description) || null,
    designInspiration: clean(d.designInspiration) || null,
    acquisition: clean(d.acquisition) || null,
    storage: clean(d.storage) || null,

    // lists
    spawnLocations: Array.isArray(d.spawnLocations) ? d.spawnLocations : [],
    gallery,
    videoUrl: d.videoUrl ?? null,

    // meta
    tags,
  };
}

export default function VehicleDetail() {
  const { id } = useParams();
  const location = useLocation();

  // state & hooks (declared unconditionally)
  const [v, setV] = useState(undefined); // undefined = loading, null = not found
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const lightboxRef = useRef(null);

  // immediate UI reaction feedback
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  const basePath = useMemo(() => ["vehicles", id], [id]);
  const bookmarkKey = useMemo(() => `vehicles:${id}`, [id]);

  // ----- Data -----
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "vehicles", id), (snap) => {
      if (!snap.exists()) return setV(null);
      const raw = snap.data();
      setV({ id: snap.id, ...normalizeVehicle(raw), _raw: raw });
    });
    return () => unsub();
  }, [id]);

  const yt = useMemo(() => (v?.videoUrl ? toYouTubeEmbed(v.videoUrl) : null), [v?.videoUrl]);

  // ----- Share/Copy -----
  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname + location.hash;
    try {
      if (navigator.share) {
        await navigator.share({ title: v?.name ?? "Vehicle", url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch {}
  }, [v?.name, location.pathname, location.hash]);

  // ----- Lightbox helpers -----
  const openLightbox = useCallback((index) => {
    setLightbox({ open: true, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, index: 0 });
  }, []);

  const nextLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = v?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index + 1) % len : 0 };
    });
  }, [v?.gallery?.length]);

  const prevLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = v?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index - 1 + len) % len : 0 };
    });
  }, [v?.gallery?.length]);

  // keyboard nav
  useEffect(() => {
    if (!lightbox.open) return;
    function onKey(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLight();
      if (e.key === "ArrowLeft") prevLight();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, nextLight, prevLight, closeLightbox]);

  // ----- Loading / 404 -----
  if (v === undefined) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-skel-card" aria-busy="true">
            <div className="vw-skel-hero" />
            <div className="vw-skel-title" />
            <div className="vw-skel-row" />
            <div className="vw-skel-section" />
          </div>
        </div>
      </PageWrapper>
    );
  }
  if (v === null) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-empty-card" role="alert">
            <div className="vw-empty-title">We can’t find that vehicle</div>
            <p className="vw-empty-sub">It may have been removed or the link is incorrect.</p>
            <Link to="/community/vehicles_weapons?tab=vehicles" className="vw-btn vw-btn--primary">← Back to Vehicles</Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // ----- Render -----
  return (
    <PageWrapper>
      <div className="vw-wrap">
        {/* Top bar */}
        <div className="vw-toprow">
          <Link to="/community/vehicles_weapons?tab=vehicles" className="vw-back" aria-label="Back">
            ← All vehicles
          </Link>
          <div className="vw-actions">
            {v.videoUrl && !yt && (
              <a className="vw-action" href={v.videoUrl} target="_blank" rel="noreferrer">▶ Open video</a>
            )}
            <button className="vw-action" onClick={onShare}>⤴ Share</button>
          </div>
        </div>

        {/* Hero */}
        <header className="vw-hero-row">
          <div className="vw-hero-left">
            <div className="vw-portrait-wrap">
              {v.portrait ? (
                <img className="vw-portrait" src={v.portrait} alt={`${v.name} image`} loading="lazy" />
              ) : (
                <div className="vw-portrait vw-portrait--placeholder" aria-label="No image">🚗</div>
              )}
            </div>
          </div>

          <div className="vw-hero-right">
            <div className="vw-chips">
              {v.make && <span className="vw-chip">🏷 {v.make}</span>}
              {v.class && <span className="vw-chip">🚘 {v.class}</span>}
              {typeof v.price === "number" && <span className="vw-chip">💵 ${Number(v.price).toLocaleString("en-US")}</span>}
              {v.seats != null && <span className="vw-chip">🪑 {v.seats} seats</span>}
              {v.drivetrain && <span className="vw-chip">🛞 {v.drivetrain}</span>}
              {v.waterCapable && <span className="vw-chip">🌊 Water-capable</span>}
              {v.boostNitro && <span className="vw-chip">⚡ Nitro</span>}
            </div>

            <h1 className="vw-title">{v.name}</h1>

            {/* optional: show selected reaction pill */}
            {selectedReactionFromBar && (
              <div className="vw-featured-reaction" aria-hidden>
                {(() => {
                  const id = selectedReactionFromBar;
                  const map = {
                    love: "❤️ Love",
                    fire: "🔥 Hot",
                    funny: "😂 Funny",
                    cool: "🆒 Cool",
                    helpful: "💡 Helpful",
                    applause: "👏 Applause",
                    curious: "❓ Curious",
                    bug: "🐞 Issue",
                  };
                  return map[id] || id;
                })()}
              </div>
            )}

            {/* Description */}
            {v.description?.trim?.() && (
              <Section id="desc">
                <div className="vw-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{v.description}</ReactMarkdown>
                </div>
              </Section>
            )}

            {/* Quick stats grid */}
            <div className="vw-facts-grid">
              {v.topSpeedKph != null && (
                <div className="vw-fact"><span>Top speed</span><strong>{v.topSpeedKph} km/h</strong></div>
              )}
              {v.accel0to100 != null && (
                <div className="vw-fact"><span>0–100 km/h</span><strong>{v.accel0to100}s</strong></div>
              )}
              {v.brake100to0m != null && (
                <div className="vw-fact"><span>100–0 km/h</span><strong>{v.brake100to0m} m</strong></div>
              )}
              {v.handling != null && (
                <div className="vw-fact"><span>Handling</span><strong>{v.handling}</strong></div>
              )}
              {v.armor != null && (
                <div className="vw-fact"><span>Armor</span><strong>{v.armor}</strong></div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="vw-grid">
          <main className="vw-main">
            {(v.acquisition?.trim?.() || v.spawnLocations?.length) && (
              <Section id="acq" title="How to Get">
                {v.acquisition?.trim?.() && (
                  <div className="vw-md">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{v.acquisition}</ReactMarkdown>
                  </div>
                )}
                {Array.isArray(v.spawnLocations) && v.spawnLocations.length > 0 && (
                  <>
                    <h3 className="vw-h3">Spawn Locations</h3>
                    <ul className="vw-list">
                      {v.spawnLocations.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </>
                )}
              </Section>
            )}

            {(v.designInspiration?.trim?.() || v.storage?.trim?.()) && (
              <Section id="notes" title="Notes">
                {v.designInspiration?.trim?.() && (
                  <>
                    <h3 className="vw-h3">Design Inspiration</h3>
                    <div className="vw-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{v.designInspiration}</ReactMarkdown></div>
                  </>
                )}
                {v.storage?.trim?.() && (
                  <>
                    <h3 className="vw-h3">Storage</h3>
                    <div className="vw-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{v.storage}</ReactMarkdown></div>
                  </>
                )}
              </Section>
            )}

            {Array.isArray(v.gallery) && v.gallery.length > 0 && (
              <Section id="gallery" title="Gallery">
                <div className="vw-gallery-grid">
                  {v.gallery.map((src, i) => (
                    <button key={i} className="vw-shotbtn" onClick={() => openLightbox(i)} aria-label={`Open image ${i + 1}`}>
                      <img className="vw-shot" src={src} alt={`Image ${i + 1}`} loading="lazy" />
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {v.videoUrl && (
              <Section id="video" title="Video">
                {yt ? (
                  <div className="vw-video">
                    <iframe
                      title="Vehicle video"
                      src={yt}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <a className="vw-link" href={v.videoUrl} target="_blank" rel="noreferrer">▶ Open video</a>
                )}
              </Section>
            )}

            {/* Engagement row */}
            <div className="vw-engage">
              <PresenceBadge presenceKey="vehicles" entityId={id} />
              <LikeBar basePath={basePath} />
              <ReactionBar
                basePath={basePath}
                visibleReactions={[
                  "love",       // Love
                  "fire",       // Hot
                  "funny",      // Funny
                  "cool",       // Cool
                  "helpful",    // Helpful
                  "applause",   // Applause
                  "bug",        // Issue (bug)
                ]}
                onSelect={(rid) => setSelectedReactionFromBar(rid)}
              />
              <Bookmark bookmarkKey={bookmarkKey} />
            </div>

            {/* Discussion (full-width within main column) */}
            <Discussion basePath={basePath} />
          </main>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && Array.isArray(v.gallery) && v.gallery.length > 0 && (
        <div ref={lightboxRef} className="vw-lightbox" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Image viewer">
          <button className="vw-lightbox-close" onClick={closeLightbox} aria-label="Close">✕</button>
          <button className="vw-lightbox-nav left" onClick={prevLight} aria-label="Previous">‹</button>
          <img className="vw-lightbox-img" src={v.gallery[lightbox.index]} alt={`Image ${lightbox.index + 1}`} />
          <button className="vw-lightbox-nav right" onClick={nextLight} aria-label="Next">›</button>
        </div>
      )}
    </PageWrapper>
  );
}
